import { useMemo } from "react";
import { doc } from "firebase/firestore";
import { useMyLessons } from "./useMyLessons";
import { useFirestoreQuery } from "./useFirestoreQuery";
import { useAuth } from "@/firebase/useAuth";
import { db } from "@/firebase/firebase-config";

const BASE_WEEKLY_GOAL = 3;
const STRETCH_GOAL = 8;

/**
 * Get the start of the current week (Monday at 6:00 AM UTC)
 */
function getWeekStart(): Date {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(now);
  monday.setUTCDate(now.getUTCDate() - daysToMonday);
  monday.setUTCHours(6, 0, 0, 0);
  
  // If we're before 6am Monday UTC, go back to previous Monday
  if (now < monday) {
    monday.setUTCDate(monday.getUTCDate() - 7);
  }
  
  return monday;
}

type MetaProgress = {
  weekStartDate?: string;
  weeklyStretchGoalAccepted?: boolean;
  weeklyStretchGoalReached?: boolean;
};

/**
 * Calculate weekly progress from myLessons collection with stretch goal support
 * Base goal: 3 lessons (+10 points)
 * Stretch goal: 8 lessons (+20 bonus points) - only if user accepts the challenge
 */
export function useWeeklyProgress() {
  const { user } = useAuth();
  const { myLessons, loading: lessonsLoading } = useMyLessons();

  // Fetch stretch goal state from meta/progress
  const progressDoc = useMemo(
    () => (user ? doc(db(), "users", user.uid, "meta", "progress") : undefined),
    [user]
  );
  const { data: metaProgress, loading: metaLoading } = useFirestoreQuery<MetaProgress>(progressDoc);

  const weeklyProgress = useMemo(() => {
    const weekStart = getWeekStart();
    const weekStartString = weekStart.toISOString();
    
    // Extract meta progress data (handle array case from useFirestoreQuery)
    const progressData = Array.isArray(metaProgress) ? metaProgress[0] : metaProgress;
    
    // Check if we're in a new week - reset stretch goal if so
    const storedWeekStart = progressData?.weekStartDate;
    const isNewWeek = !storedWeekStart || storedWeekStart !== weekStartString;
    
    let weeklyLessonsCompleted = 0;
    
    myLessons.forEach((lesson) => {
      if (lesson.status === "completed" && lesson.completedAt) {
        const completedDate = lesson.completedAt.toDate();
        if (completedDate >= weekStart) {
          weeklyLessonsCompleted++;
        }
      }
    });

    // If new week, stretch goal is reset
    const stretchGoalAccepted = isNewWeek ? false : (progressData?.weeklyStretchGoalAccepted ?? false);
    const stretchGoalReached = isNewWeek ? false : (progressData?.weeklyStretchGoalReached ?? false);
    
    // Determine active goal
    const activeGoal = stretchGoalAccepted ? STRETCH_GOAL : BASE_WEEKLY_GOAL;
    const baseGoalReached = weeklyLessonsCompleted >= BASE_WEEKLY_GOAL;
    const weeklyGoalReached = weeklyLessonsCompleted >= activeGoal;
    
    // Stretch goal eligible when base goal reached and not yet accepted
    // Note: Even if isNewWeek (no stored week), still offer if they have 3+ lessons this week
    const stretchGoalEligible = baseGoalReached && !stretchGoalAccepted;

    return {
      weeklyLessonsCompleted,
      weeklyGoalReached,
      weeklyGoal: activeGoal,
      baseGoalReached,
      stretchGoalAccepted,
      stretchGoalReached,
      stretchGoalEligible,
      weekStartDate: weekStartString,
      isNewWeek,
    };
  }, [myLessons, metaProgress]);

  return {
    ...weeklyProgress,
    loading: lessonsLoading || metaLoading,
  };
}
